import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, Stack, Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const BookingSuccess: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);

    const orderId = params.get('order_id') || params.get('orderId') || params.get('id');
    const transactionStatus = params.get('transaction_status') || params.get('status_code') || params.get('status_message');
    const statusDetail = params.get('fraud_status') || params.get('message');

    const [bookingDetails, setBookingDetails] = useState<any>(null);
    const [memberCode, setMemberCode] = useState<string>('');
    const [copyStatus, setCopyStatus] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [previousBookingPath, setPreviousBookingPath] = useState<string | null>(null);
    const [isJerseyOrder, setIsJerseyOrder] = useState<boolean>(false);

    // Redirect pending booking status to booking-pending route
    useEffect(() => {
        if (orderId && transactionStatus === 'pending') {
            navigate(`/booking-pending?order_id=${encodeURIComponent(orderId)}&status_message=${encodeURIComponent(statusDetail || 'Pembayaran belum selesai.')}`);
        }
    }, [orderId, transactionStatus, statusDetail, navigate]);

    // Hapus snap token dari localStorage jika pembayaran berhasil
    React.useEffect(() => {
        if (orderId && (transactionStatus === 'settlement' || transactionStatus === 'capture')) {
            localStorage.removeItem(`payment_${orderId}`);
        }
    }, [orderId, transactionStatus]);

    // Fetch booking details for any booking with an order_id
    useEffect(() => {
        const fetchBookingDetails = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API_ENDPOINTS.BOOKINGS}?action=booking-details&order_id=${orderId}`);
                if (response.data?.success) {
                    setBookingDetails(response.data.booking || response.data);
                    setMemberCode(response.data.memberCode || (orderId?.startsWith('MEMBER-MABAR-') ? orderId.replace(/^MEMBER-MABAR-/, '') : ''));
                    // Check if this is a jersey order
                    if (response.data.booking?.type === 'jersey' || response.data?.type === 'jersey' || orderId?.startsWith('JERSEY-')) {
                        setIsJerseyOrder(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching booking details:', error);
                if (orderId?.startsWith('MEMBER-MABAR-')) {
                    setMemberCode(orderId.replace(/^MEMBER-MABAR-/, ''));
                    setBookingDetails({ orderId });
                }
                // Fallback: check order ID prefix for jersey orders
                if (orderId?.startsWith('JERSEY-')) {
                    setIsJerseyOrder(true);
                    setBookingDetails({ orderId });
                }
            } finally {
                setLoading(false);
            }
        };

        if (orderId) {
            fetchBookingDetails();
        }
    }, [orderId]);

    useEffect(() => {
        const fetchPreviousBookingPath = async () => {
            const tempatName = bookingDetails?.booking?.tempat || bookingDetails?.tempat;
            if (!tempatName) return;

            try {
                const [gorResponse, coachingResponse] = await Promise.all([
                    axios.get(API_ENDPOINTS.GOR),
                    axios.get('/api/coaching')
                ]);

                const gorList = gorResponse.data || [];
                const coachingList = coachingResponse.data || [];

                const foundCoaching = coachingList.find((item: any) =>
                    item.id === tempatName ||
                    item.nama === tempatName ||
                    item.nama?.toLowerCase() === tempatName?.toLowerCase()
                );
                if (foundCoaching) {
                    setPreviousBookingPath(`/coaching/${foundCoaching.id}`);
                    return;
                }

                const foundGor = gorList.find((gor: any) =>
                    gor.id === tempatName ||
                    gor.nama === tempatName ||
                    gor.nama?.toLowerCase() === tempatName?.toLowerCase()
                );
                if (foundGor) {
                    setPreviousBookingPath(`/gor/${foundGor.id}`);
                }
            } catch (error) {
                console.error('Error fetching previous booking route:', error);
            }
        };

        fetchPreviousBookingPath();
    }, [bookingDetails]);

    const isMembership = orderId?.startsWith('MEMBER-MABAR-') || bookingDetails?.isMembership;
    const isPending = transactionStatus === 'pending';

    const handleCopyMemberCode = async () => {
        if (!memberCode) return;
        try {
            await navigator.clipboard.writeText(memberCode);
            setCopyStatus('Kode member berhasil disalin.');
        } catch (err) {
            console.error('Copy failed:', err);
            setCopyStatus('Gagal menyalin kode member.');
        }
        setTimeout(() => setCopyStatus(''), 3000);
    };

    useEffect(() => {
        const processMembershipCache = async () => {
            if (!orderId) return;
            if (!isMembership) return;
            if (transactionStatus !== 'settlement' && transactionStatus !== 'capture') return;

            try {
                const response = await axios.get(`${API_ENDPOINTS.BOOKINGS}?action=process-membership-cache&order_id=${encodeURIComponent(orderId)}`);
                if (response.data?.success) {
                    console.log('Membership cache processed successfully:', response.data.message);
                } else {
                    console.warn('Membership cache processing returned warning:', response.data);
                }
            } catch (error) {
                console.error('Error processing membership cache:', error);
            }
        };

        processMembershipCache();
    }, [orderId, isMembership, transactionStatus]);

    const pageTitle = isJerseyOrder
        ? isPending
            ? 'Pembayaran Jersey Menunggu Pembayaran'
            : 'Pembayaran Jersey Berhasil!'
        : isMembership
            ? isPending
                ? 'Membership Menunggu Pembayaran'
                : 'Membership Berhasil!'
            : isPending
                ? 'Booking Menunggu Pembayaran'
                : 'Booking Berhasil!';

    const alertSeverity = isPending ? 'info' : 'success';
    const alertMessage = isJerseyOrder
        ? isPending
            ? 'Pembayaran jersey via Midtrans belum dilakukan. Pesanan jersey Anda sementara belum diproses sampai pembayaran dikonfirmasi.'
            : 'Pembayaran jersey via Midtrans berhasil! Terima kasih telah memesan jersey RaketerS. Jersey akan diproses sesuai jadwal.'
        : isMembership
            ? isPending
                ? 'Pembayaran membership via Midtrans belum dilakukan. Membership Anda sementara belum aktif sampai pembayaran dikonfirmasi.'
                : 'Pembayaran membership via Midtrans berhasil! Selamat bergabung sebagai member RaketerS.'
            : isPending
                ? 'Pembayaran via Midtrans belum dilakukan, nama Anda sementara muncul di Daftar Reservasi sampai pembayaran dilakukan.'
                : 'Pembayaran via Midtrans berhasil, silakan cek nama Anda seharusnya muncul di Daftar Pemain Utama.';

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Box sx={{ mt: 4, p: 4, borderRadius: 3, backgroundColor: 'background.paper', boxShadow: 3 }}>
                <Typography variant="h4" sx={{ mb: 2, textAlign: 'center' }}>
                    {pageTitle}
                </Typography>

                <Alert severity={alertSeverity} sx={{ mb: 3 }}>
                    {alertMessage}
                </Alert>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {memberCode && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Kode Member Anda: <strong style={{ fontSize: '1.2em', color: '#1976d2' }}>{memberCode}</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Simpan kode member ini untuk digunakan saat booking mabar. Kode ini bersifat unik dan tidak dapat diubah.
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button variant="contained" size="small" onClick={handleCopyMemberCode}>
                                Salin Kode Member
                            </Button>
                            {copyStatus && (
                                <Typography variant="body2" sx={{ color: 'success.main' }}>
                                    {copyStatus}
                                </Typography>
                            )}
                        </Stack>
                    </Alert>
                )}

                {orderId && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Order ID: <strong>{orderId}</strong>
                    </Typography>
                )}
                {transactionStatus && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Status transaksi: <strong>{transactionStatus}</strong>
                    </Typography>
                )}
                {statusDetail && (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Detail: <strong>{statusDetail}</strong>
                    </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
                    {isJerseyOrder ? (
                        <>
                            <Button variant="contained" onClick={() => navigate('/jersey')}>Kembali ke Halaman Jersey</Button>
                            <Button variant="outlined" onClick={() => navigate('/')}>Kembali ke Beranda</Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    if (previousBookingPath) {
                                        navigate(previousBookingPath);
                                    } else {
                                        navigate('/');
                                    }
                                }}
                            >
                                Kembali ke Booking Sebelumnya
                            </Button>
                            <Button variant="outlined" onClick={() => navigate('/')}>Kembali ke Beranda</Button>
                            <Button variant="outlined" onClick={() => navigate('/member')}>Booking Member</Button>
                        </>
                    )}
                </Stack>
            </Box>
        </Container>
    );
};

export default BookingSuccess;
